import React, { useState, createRef, useEffect, RefObject } from 'react';
import * as R from 'ramda';
import { animated } from 'react-spring';
import Box from 'ui/Box';
import {
  getFilterState as filterStateSelector,
  getToggleState as toggleStateSelector,
  filterApplied,
  toggleApplied,
  getSelectedGroupId,
  getProductsForGroup
} from 'ducks/products';
import { FilterType } from 'rangeConfig/@types';
import { useSprings } from 'react-spring';
import { useSelector } from 'react-redux';
import usePrevious from 'hooks/usePrevious';
import { useIntl } from 'react-intl';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { Product } from '@inter-ikea-storageone/range';
import { filterHasCorrectOptionsLength } from 'filters/SizeFilter/utils';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { isMobile } from 'utils/platform';
import Divider from 'ui/Divider/Divider';
import styled from 'styled-components';
import media from 'ui/media';
import { SpaceProps } from 'styled-system';
import { useAppDispatch } from 'ducks/hooks';
import Title, { HeadingType } from 'ui/Typography/Title';

const FilterTitle = styled(Title)`
  margin-bottom: ${({ theme }) => theme.space.md};
  ${media.mobile`
    margin-bottom: 1.5rem;
  `}
`;

type Props = SpaceProps & {
  isFilterVisible?: boolean;
  dividerVisible?: boolean;
  shouldAnimate?: boolean;
  productsToShow?: Product[];
};

type FilterRef = {
  [index: number]: RefObject<HTMLDivElement>;
};

const springConfig = {
  mass: 1,
  tension: 280,
  friction: 26,
  clamp: true
};

function Filters({ isFilterVisible, shouldAnimate, productsToShow, ...props }: Props) {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const products = useSelector(getProductsForGroup);
  const getFilterState = useSelector(filterStateSelector);
  const getToggleState = useSelector(toggleStateSelector);
  const selectedGroupId = useSelector(getSelectedGroupId);

  const prevFilterVisibleRef = usePrevious(isFilterVisible);

  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const [filterRefs, setFilterRefs] = useState<FilterRef | null>({});

  const filterAction = (type: string) => {
    if (type === FilterType.TOGGLE) {
      return toggleApplied;
    }
    return filterApplied;
  };

  const filterStateGetter = (type: string) => {
    if (type === FilterType.TOGGLE) {
      return getToggleState;
    }
    return getFilterState;
  };

  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);
  const prevSelectedGroup = usePrevious(selectedGroup);

  const filters =
    selectedGroup && selectedGroup.mainMenu.filters ? [...selectedGroup.mainMenu.filters] : [];

  // useSprings hack to fake a static list
  const maxNumberOfFilters = filters.length;

  const [springs, setSprings] = useSprings(maxNumberOfFilters, () => ({
    from: {
      height: isFilterVisible ? 'auto' : 0,
      opacity: isFilterVisible ? 1 : 0
    },
    to: {
      height: isFilterVisible ? 'auto' : 0,
      opacity: isFilterVisible ? 1 : 0
    },
    config: springConfig
  }));

  useEffect(() => {
    const refs: any = {};
    for (let i = 0; i <= filters.length; i++) {
      refs[i] = createRef();
    }
    setFilterRefs(refs);
  }, [filters.length]);

  useEffect(() => {
    const hasRefs = filterRefs && Object.keys(filterRefs).length > 0;
    if (filterRefs && hasRefs && shouldAnimate) {
      const heights = Object.values(filterRefs).map((ref: any) => {
        if (ref.current) {
          const bounds = ref.current.getBoundingClientRect();
          return bounds.height;
        }
        return 0;
      });

      // Just return so the animation won't rerun when the filter haven't changed
      if (prevFilterVisibleRef === isFilterVisible) return;

      setSprings.start((index: number) => ({
        from: {
          opacity: isFilterVisible ? 0 : 1,
          height: isFilterVisible ? 0 : heights[index],
          pointerEvents: isFilterVisible ? 'none' : 'all'
        },
        to: {
          opacity: isFilterVisible ? 1 : 0,
          height: isFilterVisible ? heights[index] : 0,
          pointerEvents: isFilterVisible ? 'all' : 'none'
        },
        reset: isFilterVisible && prevSelectedGroup !== selectedGroup,
        config: springConfig
      }));
    }
  }, [
    filterRefs,
    isFilterVisible,
    prevSelectedGroup,
    selectedGroup,
    setSprings,
    shouldAnimate,
    prevFilterVisibleRef
  ]);

  const checkedFilters = filters.filter(filter =>
    filterHasCorrectOptionsLength(filter.id, products as Product[])
  );

  return (
    <Box {...props}>
      {filterRefs &&
        springs.map((springProps: any, index: number) => (
          <div key={index}>
            {checkedFilters[index] && // needed due to useSprings hack
              springProps && (
                <animated.div
                  style={{
                    ...springProps,
                    visibility: springProps.opacity.to((opacity: any) =>
                      opacity === 0 ? 'hidden' : 'visible'
                    )
                  }}
                >
                  <Box ref={filterRefs[index]} p="s">
                    {selectedGroup &&
                      [checkedFilters[index]].map(
                        ({ component: Component, title, type, id, ...props }) => (
                          <div key={id}>
                            <FilterTitle
                              id={id}
                              fontWeight="bold"
                              type={ThemeFontStyleTypeEnum.headingXSmall}
                              heading={HeadingType.HeadingThree}
                            >
                              {title && formatMessage({ id: title })}
                            </FilterTitle>
                            <Component
                              onFilter={(productIdList: string[], filterState: any) => {
                                dispatch(
                                  filterAction(type)({
                                    id: id,
                                    productGroupId: selectedGroup.id,
                                    productIdList,
                                    filterState
                                  })
                                );
                              }}
                              isFilterVisible={isFilterVisible}
                              products={products}
                              productsToShow={productsToShow}
                              filterState={filterStateGetter(type)(selectedGroup.id, id)}
                              type={type}
                              {...props}
                              my={'xl'}
                              id={id}
                            />
                            {isMobile() && <Divider hidden={index === checkedFilters.length - 1} />}
                          </div>
                        )
                      )}
                  </Box>
                </animated.div>
              )}
          </div>
        ))}
    </Box>
  );
}

export default Filters;
